import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { Subscription, combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AuthService } from './core/auth/auth.service';
import { SeoService } from './core/services/seo.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private router = inject(Router);
  private seoService = inject(SeoService);
  private subscriptions: Subscription[] = [];

  // Public routes that don't require authentication
  private publicRoutes = [
    '/auth/login',
    '/auth/register',
    '/public/',
    '/error'
  ];

  // Routes that are handled by Angular router redirects (not by auth logic)
  private routerHandledPaths = ['/', ''];

  ngOnInit() {
    this.seoService.initialize();
    this.setupAuthRedirectListener();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Centralized auth redirect logic
   *
   * This handles:
   * 1. User authenticated on public auth route → Redirect to /dashboard
   * 2. User logs out on protected route → Redirect to /auth/login
   * 3. Auth errors → Redirect to /auth/login
   */
  private setupAuthRedirectListener() {
    // Track if this is the initial load or a state change (for logout detection)
    let previousUser: any = undefined; // undefined = not yet initialized

    // Listen for auth state changes after initialization
    const authSub = combineLatest([
      this.authService.authInitialized$,
      this.authService.currentUser$
    ]).pipe(
      filter(([initialized]) => initialized === true)
    ).subscribe(([_, user]) => {
      const currentUrl = this.router.url;

      // Skip auth redirect logic for paths handled by Angular router (like '/' → '/dashboard')
      if (this.isRouterHandledPath(currentUrl)) {
        console.log('[App] Skipping auth redirect for router-handled path', { currentUrl });
        previousUser = user;
        return;
      }

      if (user) {
        // User is authenticated
        // If on a public auth route, redirect to dashboard
        if (this.isPublicAuthRoute(currentUrl)) {
          console.log('[App] User authenticated on public route, redirecting to dashboard', {
            from: currentUrl,
            userId: user.id
          });
          this.router.navigate(['/dashboard']);
        }
        // If on protected route, authGuard already allowed access
      } else {
        // User is not authenticated (either initial load with no session, or logout)
        const isLogout = previousUser !== undefined && previousUser !== null;

        if (isLogout) {
          // User was logged in and is now logged out → redirect to login
          console.log('[App] User logged out, redirecting to login', {
            from: currentUrl
          });
          this.router.navigate(['/auth/login']);
        } else if (!this.isPublicRoute(currentUrl)) {
          // Initial load on protected route with no session
          // authGuard will handle this, but log for debugging
          console.log('[App] No session on protected route (initial load)', {
            currentUrl
          });
        }
      }

      // Track previous user state for logout detection
      previousUser = user;
    });

    this.subscriptions.push(authSub);

    // Also listen for auth errors to handle error display or logging
    const errorSub = this.authService.authError$.pipe(
      filter(error => error !== null)
    ).subscribe(error => {
      console.log('[App] Auth error occurred', error);
      // If not already on login page, redirect there
      if (!this.router.url.includes('/auth/login')) {
        this.router.navigate(['/auth/login']);
      }
    });

    this.subscriptions.push(errorSub);
  }

  /**
   * Check if the URL is a public auth route (login, register)
   */
  private isPublicAuthRoute(url: string): boolean {
    return url.startsWith('/auth/') || url === '/login';
  }

  /**
   * Check if the URL is any public route
   */
  private isPublicRoute(url: string): boolean {
    return this.publicRoutes.some(route => url.startsWith(route));
  }

  /**
   * Check if the URL is handled by Angular router redirects
   * These paths will redirect to other routes, so we skip auth logic for them
   */
  private isRouterHandledPath(url: string): boolean {
    return this.routerHandledPaths.includes(url);
  }
}
